import { tokenDeclarations } from './tokens.js';

/**
 * ビューアのスタイル。
 *
 * **Shadow root の内側に閉じる**([ADR-0017](../../../../docs/adr/0017-viewer-embed-boundary.md))。
 * 埋め込み先のグローバル CSS を汚さず、埋め込み先の CSS からも影響を受けない。
 *
 * V02(単体ページ)は Shadow を使わないが、**同じスタイル文字列を
 * `<style>` に入れて共有する** —— 見た目が V01 と一致していることが
 * 「iframe 埋め込みの実体」(ADR-0003)として成立する前提。
 */
/**
 * ステージの高さをビューポートに収める上限(#212)。**この順に適用する。**
 *
 * ## 順序が意味を持つ
 *
 * **新しい単位を後に置く。** 同じプロパティに順に代入すると、
 * **非対応のブラウザは解釈できない値を捨てる** —— 後ろが捨てられて
 * 前が残る。逆に並べると、**`dvh` に対応したブラウザで `vh` が勝ってしまう。**
 *
 * ## `vh` だけでは足りない
 *
 * モバイルの `vh` は**アドレスバーを含まない最大の高さ**で、バーが出ている
 * 間は画面より大きい —— それだけでスクロールが生まれる。報告された
 * 「タブなどではみ出す」がまさにこれ。**`dvh` はいま見えている高さに追従する。**
 *
 * ## `vh` も外せない
 *
 * `dvh` は `vh` より新しく、**古い環境では無効値として捨てられる** ——
 * 落ちる先が無いと高さが無制限に戻る。
 */
export const VIEWPORT_HEIGHT_LIMITS = ['100vh', '100dvh'] as const;

export function viewerStyles(): string {
  return `
:host, .signa-root {
${tokenDeclarations()}
  display: block;
  position: relative;
  width: 100%;
  color: var(--sp-text-primary);
  font-family: system-ui, -apple-system, 'Segoe UI', 'Hiragino Sans', 'Noto Sans JP', sans-serif;
  font-size: 14px;
  line-height: 1.5;
  box-sizing: border-box;
}

.signa-root *, .signa-root *::before, .signa-root *::after {
  box-sizing: inherit;
}

.signa-frame {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  width: 100%;
  min-height: 120px;
  background: var(--sp-surface-page);
  border-radius: var(--signa-radius);
  overflow: hidden;
}

/* 状態表示(ローディング・エラー)。ステージの上に重ねる。 */
.signa-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px;
  text-align: center;
  color: var(--sp-text-secondary);
}

.signa-status[hidden] { display: none; }

.signa-status-error { color: var(--sp-text-primary); }

.signa-button {
  appearance: none;
  padding: 8px 16px;
  border: 1px solid var(--sp-border-strong);
  border-radius: var(--signa-radius);
  background: var(--sp-surface-raised);
  color: var(--sp-text-primary);
  font: inherit;
  cursor: pointer;
}

.signa-button:hover { background: var(--sp-surface-overlay-weak); }

.signa-button:focus-visible {
  outline: 2px solid var(--signa-accent);
  outline-offset: 2px;
}

/* D&D の受け口。ステージの上に重ねる。 */
.signa-ui {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px;
  pointer-events: none;
}

/* 受け口自体はクリックできる必要がある。 */
.signa-ui > * { pointer-events: auto; }

/*
 * D&D の見せ方(受け口の案内とドロップ先の輪郭)。
 *
 * pointer-events を切る。この要素は inset:0 で UI 全体を覆い、
 * position 指定があるぶん静的な兄弟(操作の帯)より上に描かれる ——
 * auto のままだと再生 / 停止 / 同期のクリックを奪う(#159 の退行)。
 *
 * D&D の受け口は signa-root なので、ここが受ける必要はない。
 */
.signa-dropzone {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

/* 操作の帯は覆いより上に置く(押せることを位置でも担保する)。 */
.signa-actions {
  position: relative;
  z-index: 1;
}

/*
 * **画面全体は縁取らない**(#124)。ドロップ先の枠だけを光らせる ——
 * 全体を覆うと「どこに落とせばよいか」が分からない。
 * 残すのは注意文(signa-dropzone-label)の表示だけ。
 */
.signa-drop-outline {
  position: absolute;
  pointer-events: none;
  overflow: visible;
  opacity: 0;
  transition: opacity 120ms ease;
}

.signa-dropzone[data-active='true'] .signa-drop-outline { opacity: 1; }

.signa-drop-outline-shape {
  fill: var(--sp-accent-primary-surface);
  stroke: var(--signa-accent);
  stroke-width: 3;
  stroke-dasharray: 8 5;
}

.signa-dropzone-label {
  padding: 8px 12px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  color: var(--sp-text-primary);
  opacity: 0;
  transition: opacity 120ms ease;
}

.signa-dropzone[data-active='true'] .signa-dropzone-label { opacity: 1; }

/*
 * **空のセルに出す案内**(#208)。描画コアが SVG の text として置く。
 *
 * **静止状態で出る**のが要点。signa-dropzone-label はドラッグ中しか
 * 見えず(data-active)、**すでにドラッグしている人にしか届かない** ——
 * こちらは何もしていない訪問者に「ここに入れられる」と伝える。
 *
 * **見た目は描画コアに持たせない。** 埋め込み先が調整できる口を残すため
 * (--signa-accent と同じ考え方 — #33)。
 */
.signa-outline-hint {
  fill: var(--sp-text-primary);
  font-size: 13px;
  /* 背景写真の明暗にかかわらず読めるように縁を付ける。 */
  paint-order: stroke;
  stroke: var(--sp-surface-overlay-strong);
  stroke-width: 4;
  stroke-linejoin: round;
  /* 枠は pointer-events: none の SVG の中にある。文字も同じ。 */
  user-select: none;
}

/*
 * **どちらを見せるかは入力方式で決める**(#208)。
 *
 * **タッチ端末では D&D で素材を入れられない**(#159)ので、「ここにドロップ」
 * ではなく「PC で試せる」と伝える。
 *
 * **JS で判定しない。** ontouchstart や maxTouchPoints は**タッチ対応の
 * ノート PC を誤判定**するうえ、判定した時点の状態で固定される ——
 * メディアクエリなら**いま使っている入力方式**に追従する。
 */
.signa-outline-hint-touch {
  display: none;
}

@media (hover: none) and (pointer: coarse) {
  .signa-outline-hint-drop {
    display: none;
  }

  .signa-outline-hint-touch {
    display: block;
  }
}

/* 「送信されません」の注意。**素材を当てるまで表示**(#159 で常時表示から変更)。 */
.signa-notice {
  padding: 4px 10px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  color: var(--sp-text-primary);
  font-size: 12px;
}

.signa-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  /* 埋め込み先が狭いこともある。**折り返してページを押し広げない**(#159)。 */
  flex-wrap: wrap;
  justify-content: center;
}

/* 再生 / 停止 / 同期(#159)。**素材が当たるまで出さない。** */
.signa-controls {
  display: flex;
  gap: 8px;
  align-items: center;
}

/*
 * display を指定した要素には [hidden] を書き添える。
 *
 * hidden 属性の display:none は UA スタイルシートのもので、
 * 作者の display:flex に負ける —— 書かないと hidden にしても消えない。
 *
 * .signa-ui はこれが抜けていて、動画枠が 0 個のときに D&D の受け口が
 * 消えていなかった(#159 で気付いた既存の不具合)。
 */
.signa-ui[hidden],
.signa-dropzone[hidden],
.signa-actions[hidden],
.signa-controls[hidden] { display: none; }

.signa-notice[hidden] { display: none; }

.signa-button[disabled] {
  opacity: 0.5;
  cursor: default;
}

.signa-button[disabled]:hover { background: var(--sp-surface-raised); }

.signa-message {
  padding: 4px 10px;
  border-radius: var(--signa-radius);
  background: var(--sp-surface-overlay-strong);
  font-size: 12px;
}

.signa-message[hidden] { display: none; }

/*
 * 素材の要素の置き場。
 *
 * **display: none にしない**(復号が止まる実装がある)。
 *
 * **面積を潰さない**(#168 / PoC 3.5 の申し送り 1)。以前は 1px で、
 * 事実上不可視の動画の復号優先度が下げられている疑いがあった ——
 * 「一部の動画だけ進みが遅い」の原因候補。小さく保ちつつ面積を持たせる。
 *
 * **並べる。重ねない。** 全部を同じ位置に置くと 1 本を除いて完全に隠れ、
 * 「不可視なら優先度が下がる」という仮説のもとでは**まさに一部だけが遅くなる。**
 */
.signa-videos {
  position: absolute;
  left: 0;
  top: 0;
  width: 160px;
  height: 90px;
  display: flex;
  flex-wrap: wrap;
  align-content: flex-start;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
  .signa-dropzone, .signa-dropzone-label, .signa-drop-outline { transition: none; }
}
`.trim();
}
